import React from 'react';
import { Grid, Paper, Stack, Chip, Typography, Divider, Button, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SortIcon from '@mui/icons-material/Sort';
import FilterDropdown from './FilterDropdown';
import { COLOR_PRIMARY_2 } from 'src/colors';
import DownloadIcon from '@mui/icons-material/Download';
import { formatCurrency } from 'src/utils/functions';
import { exchangeRates } from 'src/const';
import TooltipNew from 'src/App/HOC/Tooltips/TooltipNew';

// Define the shape of options used in filters
interface FilterOption {
  value: string;
  label: string;
  isState?: boolean;
  countryCode?: string;
  icon?: any;
  tooltip?: string;
}

// Define common button styles (can be shared or defined locally)
const commonButtonSx = {
  justifyContent: 'flex-start',
  boxShadow: 'none',
  height: '36.5px', // Match typical Button height
  minWidth: 'auto', // Adjust as needed
};
const outlinedButtonSx = {
  borderColor: 'divider', // Use divider color for subtle border
  color: 'text.primary',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'action.hover',
    borderColor: 'divider',
  },
};

// Define the props for the SelectedFiltersDisplay component - UPDATED
interface SelectedFiltersDisplayProps {
  selectedRegions: string[];
  regionOptions: FilterOption[];
  handleRemoveRegion: (value: string) => void;
  selectedLocations: string[];
  selectedRegulations: string[];
  selectedMarketSizes: string[];
  selectedSortBy: string;
  selectedBudget: {
    type: 'buy' | 'rent';
    range: { min: number; max: number };
  };
  countryOptions: FilterOption[];
  regulationOptions: FilterOption[];
  marketSizeOptions: FilterOption[];
  sortByOptions: FilterOption[];
  findLabel: (options: FilterOption[], value: string) => string;
  handleRemoveLocation: (value: string) => void;
  handleRemoveRegulation: (value: string) => void;
  handleRemoveMarketSize: (value: string) => void;
  handleRemoveBudget: () => void;
  handleSortByChange: (value: string | string[]) => void;
  isMobileScreen: boolean;
  isFreeUser: boolean;
  handleOnSampleClick: (plan: string) => void;
  currency: string;
}

const SelectedFiltersDisplay: React.FC<SelectedFiltersDisplayProps> = ({
  selectedRegions,
  regionOptions,
  handleRemoveRegion,
  selectedLocations,
  selectedRegulations,
  selectedMarketSizes,
  selectedSortBy,
  selectedBudget,
  countryOptions,
  regulationOptions,
  marketSizeOptions,
  sortByOptions,
  findLabel,
  handleRemoveLocation,
  handleRemoveRegulation,
  handleRemoveMarketSize,
  handleRemoveBudget,
  handleSortByChange,
  isMobileScreen,
  isFreeUser,
  handleOnSampleClick,
  currency,
}) => {
  const hasRegions = selectedRegions.length > 0;
  const hasLocations = selectedLocations.length > 0;
  const hasRegulations = selectedRegulations.length > 0;
  const hasMarketSizes = selectedMarketSizes.length > 0;
  const hasBudget = selectedBudget.range.min > 0 || selectedBudget.range.max > 0;
  const hasActiveFilters = hasLocations || hasRegulations || hasMarketSizes || hasBudget;

  const selectedSortByLabel = findLabel(sortByOptions, selectedSortBy) || 'Sort By';
  const hasAnyActiveContent = hasActiveFilters || selectedSortBy;

  const getBudgetLabel = () => {
    const { type, range } = selectedBudget;
    return `${type === 'buy' ? 'Buy' : 'Rent'}: ${exchangeRates[currency]?.symbol ||
      ''} ${formatCurrency(String(range.min))} - ${exchangeRates[currency]?.symbol ||
      ''} ${formatCurrency(String(range.max))}`;
  };

  if (!hasAnyActiveContent) {
    return null; // Don't render if nothing is selected/sorted
  }

  return (
    <Grid item xs={12} sx={{ mt: 0 }}>
      <Paper
        elevation={0}
        sx={{
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              width: isMobileScreen ? '100%' : 'calc(100% - 770px)',
            }}
          >
            {/* Display Individual Filter Chips */}
            {hasActiveFilters && (
              <Typography
                variant="body2"
                sx={{ fontWeight: 'medium', color: 'text.secondary', mr: 1, flexShrink: 0 }}
              >
                Filters:
              </Typography>
            )}
            {/* Region chips before location */}
            {selectedRegions.map(value => (
              <Chip
                key={`region-${value}`}
                label={findLabel(regionOptions, value)}
                size="small"
                onDelete={() => handleRemoveRegion(value)}
              />
            ))}
            {selectedLocations.map(value => (
              <Chip
                key={`loc-${value}`}
                label={findLabel(countryOptions, value)}
                size="small"
                onDelete={() => handleRemoveLocation(value)}
              />
            ))}
            {selectedRegulations.map(value => (
              <Chip
                key={`reg-${value}`}
                label={findLabel(regulationOptions, value)}
                size="small"
                onDelete={() =>
                  isFreeUser ? handleOnSampleClick('expert') : handleRemoveRegulation(value)
                }
              />
            ))}
            {selectedMarketSizes.map(value => (
              <Chip
                key={`size-${value}`}
                label={findLabel(marketSizeOptions, value)}
                size="small"
                onDelete={() => handleRemoveMarketSize(value)}
              />
            ))}
            {hasBudget && (
              <Chip
                key="budget"
                label={getBudgetLabel()}
                size="small"
                onDelete={handleRemoveBudget}
              />
            )}
          </Box>

          <Box
            sx={
              isMobileScreen
                ? {
                    marginLeft: 'auto !important',
                    marginTop: '10px !important',
                    overflowX: 'auto',
                    width: '100%',
                  }
                : {
                    width: '750px',
                  }
            }
          >
            {/* Sort By Chips */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                borderLeft: '1px solid #ccc',
                pl: '10px',
                ...(isMobileScreen && { width: '750px' }),
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 'medium', color: 'text.secondary', mr: 1, flexShrink: 0 }}
              >
                Sort by:
              </Typography>
              {sortByOptions.map(option => (
                <TooltipNew key={option.value} title={''} content={option.tooltip || ''}>
                  <Chip
                    key={option.value + 'chip'}
                    label={option.label}
                    clickable
                    color="primary"
                    variant={selectedSortBy === option.value ? 'filled' : 'outlined'}
                    onClick={() => handleSortByChange(option.value)}
                    icon={option.icon}
                    sx={{
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      height: 28,
                      minWidth: 0,                    
                      ...(selectedSortBy === option.value
                        ? {
                            px: '4px',
                            backgroundColor: '#4FA2B1', // COLOR_PRIMARY_2_600
                            color: '#fff',
                            boxShadow: '0 1px 4px 0 rgba(4, 50, 80, 0.10)',
                          }
                        : {
                            px: '0px',
                            mx: 0,
                            backgroundColor: 'transparent',
                            color: '#037A90', // COLOR_PRIMARY_2
                            border: 'none',
                            boxShadow: 'none',
                            '& .MuiChip-label': {
                              paddingRight: '2px !important',                              
                            },
                          }),
                    }}
                  />
                </TooltipNew>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
};

export default SelectedFiltersDisplay;
